import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import ParticipantList from '../components/ParticipantList';
import Button from '../components/Button';
import BackButton from '../components/BackButton';
import ShareLink from '../components/ShareLink';
import { LobbySkeleton } from '../components/Skeleton';
import PageTransition from '../components/PageTransition';
import SessionProgress from '../components/SessionProgress';
import { joinSession } from '../services/socket';
import { getSocket } from '../services/socket';
import { Participant } from '../types';

const SessionLobbyPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { session, participantId, loadSession, addParticipant, setParticipantId } = useSession();
  const [shareLink, setShareLink] = useState('');

  useEffect(() => {
    if (!sessionId) return;
    loadSession(sessionId);

    // Restore participant ID from localStorage
    const storedPid = localStorage.getItem(`session_${sessionId}_pid`);
    if (storedPid) {
      setParticipantId(storedPid);
    }

    const storedLink = localStorage.getItem(`session_${sessionId}_link`);
    if (storedLink) {
      setShareLink(storedLink);
    } else {
      setShareLink(`${window.location.origin}/session/${sessionId}/join`);
    }
  }, [sessionId, loadSession, setParticipantId]);

  // Socket connection
  useEffect(() => {
    if (!sessionId || !participantId) return;

    joinSession(sessionId, participantId);

    const socket = getSocket();

    socket.on('participant-joined', ({ participant }: { participant: Participant }) => {
      addParticipant(participant);
    });

    // Когда создатель нажимает "Начать", все участники переходят к свайпам
    socket.on('session-started', () => {
      navigate(`/session/${sessionId}/swipe`);
    });

    return () => {
      socket.off('participant-joined');
      socket.off('session-started');
    };
  }, [sessionId, participantId, addParticipant, navigate]);

  const isCreator = session?.participants.find(
    (p) => p.id === participantId
  )?.isCreator;

  const handleStart = () => {
    if (!sessionId) return;
    const socket = getSocket();
    socket.emit('start-session', { sessionId });
  };

  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 px-4 py-6">
        <LobbySkeleton />
      </div>
    );
  }

  return (
    <PageTransition className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 px-4 py-6">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <BackButton onClick={() => navigate(-1)} />
          <h1 className="text-2xl font-bold text-gray-900">Лобби</h1>
        </div>
        <SessionProgress step={2} />
        <p className="text-sm text-gray-500 mb-6">
          Ждём участников... ({session.participants.length} чел.)
        </p>

        {/* Share Link */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Пригласи друзей по ссылке:
          </h3>
          <ShareLink link={shareLink} />
        </div>

        {/* Participants */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Участники:</h3>
          <ParticipantList participants={session.participants} />
        </div>

        {/* Restaurant count */}
        {session.maxRestaurants !== null ? (
          <div className="bg-white rounded-xl p-4 shadow-sm border mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-center flex-1">
                <p className="text-3xl font-bold text-gray-400">{session.restaurantCount}</p>
                <p className="text-xs text-gray-400">в пуле</p>
              </div>
              <svg className="w-5 h-5 text-orange-300 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
              <div className="text-center flex-1">
                <p className="text-3xl font-bold text-orange-500">{Math.min(session.maxRestaurants, session.restaurantCount)}</p>
                <p className="text-xs text-gray-500">будут в свайпе</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 text-center border-t border-gray-100 pt-2 mt-1">
              При старте выберем топ {session.maxRestaurants} с учётом предпочтений участников
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl p-4 shadow-sm border mb-6 text-center">
            <p className="text-3xl font-bold text-orange-500">{session.restaurantCount}</p>
            <p className="text-sm text-gray-500">заведений выбрано</p>
          </div>
        )}

        {/* Start button */}
        {isCreator && (
          <Button size="lg" fullWidth disabled={session.restaurantCount === 0} onClick={handleStart}>
            Начать выбор
          </Button>
        )}

        {!isCreator && (
          <div className="text-center py-4">
            <p className="text-gray-500">
              Ожидайте — создатель сессии начнёт выбор
            </p>
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default SessionLobbyPage;
